
    // IMPORTS
    import { defineComponent, computed, PropType, Ref, ref } from 'vue'

    import { DropDownList } from "@progress/kendo-vue-dropdowns"
    import { Error } from "@progress/kendo-vue-labels"

    import { DropDownListChangeEvent, DropDownListFilterChangeEvent } from '@progress/kendo-vue-dropdowns'
    import { filterBy, FilterDescriptor } from '@progress/kendo-data-query'
    import { buildLocalization, StringUtil, useFieldValidation } from '@/util'

    export default defineComponent({
        name: 'inputDropDown',

        components: {
            error: Error,
            'k-drop-down': DropDownList
        },

        props: {
            id: { type: String as PropType<string>, required: true },
            modelValue: { type: String as PropType<string>, required: true },
            dataItems: { type: Array as PropType<any[]>, required: true },
            valueField: { type: String as PropType<string>, required: true },
            textField: { type: String as PropType<string>, required: true },
            label: { type: String as PropType<string>, required: false, default: () => null },
            placeholder: { type: String as PropType<string>, required: false, default: () => null },
            filterable: { type: Boolean as PropType<boolean>, required: false, default: () => false },
            required: { type: Boolean as PropType<boolean>, required: false, default: () => false },
            validator: { type: Function as PropType<(value: string) => string | null>, required: false, default: () => null },
            width: { type: Number as PropType<number | null>, required: false, default: () => null }
        },

        emits: {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            'update:modelValue': (value: string) => true,
        },

        setup(props, { emit }) {
            /********************************************************************************************************************
             STATE
             ********************************************************************************************************************/
            const { localize } = buildLocalization()
            const filter: Ref<FilterDescriptor | null> = ref(null)

            const { showError, errorMessage, errorId, clearError } = useFieldValidation(props.id, () => {
                if (props.required && StringUtil.isNullOrEmpty(props.modelValue)) return localize('requiredEntry')
                if (props.validator) return props.validator(props.modelValue)
                return null
            })

            /********************************************************************************************************************
             COMPUTED DATA
             ********************************************************************************************************************/
            const currentValue = computed(() => props.modelValue == '' ? null : props.modelValue)

            const filteredData = computed(() => filter.value == null
                ? props.dataItems.slice()
                : filterBy(props.dataItems.slice(), filter.value))

            const widthStyle = computed(() => props.width == null
                ? {}
                : { width: `${props.width}px` }
            )

            const computedLabel = computed(() => props.label
                ? props.required
                    ? props.label
                    : `${props.label} (${localize('optional')})`
                : null
            )

            const computedPlaceholder = computed(() => props.required
                ? props.placeholder
                : `${props.placeholder} (${localize('optional')})`
            )

            const defaultItem = computed(() => props.placeholder == null
                ? undefined
                : { [props.valueField]: null, [props.textField]: computedPlaceholder.value }
            )

            const computedData = { currentValue, filteredData, defaultItem, widthStyle, computedLabel, showError, errorMessage, errorId }

            /********************************************************************************************************************
             EVENT HANDLERS
             ********************************************************************************************************************/
            function onFilterChange(event: DropDownListFilterChangeEvent) {
                filter.value = event.filter
            }

            function onChange(event: DropDownListChangeEvent) {
                clearError()
                emit('update:modelValue', event.value)
            }

            const publicMethods = { onFilterChange, onChange }

            /********************************************************************************************************************
             COMPONENT INTERFACE
             ********************************************************************************************************************/
            return { ...computedData, ...publicMethods }
        }
    });
